import type { Translations } from '@/types/database';

/**
 * Applies translations to content based on the current language.
 * Falls back to the original content if no translation is available.
 *
 * @param content - The original content object (Norwegian by default)
 * @param language - The current language code ('no' or 'en')
 * @returns The content with translations applied
 */
export function applyTranslations<T extends { translations?: Translations<Partial<T>> }>(
  content: T,
  language: string
): T {
  // If language is Norwegian or no translations exist, return original
  if (language === 'no' || !content.translations) {
    return content;
  }

  const translation = content.translations[language];
  if (!translation) {
    return content;
  }

  // Merge translations over the original content
  return {
    ...content,
    ...translation,
  };
}

/**
 * Applies translations to an array of content items.
 */
export function applyTranslationsToArray<T extends { translations?: Translations<Partial<T>> }>(
  items: T[],
  language: string
): T[] {
  return items.map(item => applyTranslations(item, language));
}
